import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../trpc.js';
import { Alerta, Cartao, Etiqueta } from '../comum/ui.js';

/**
 * CADASTROS — rubricas e config sindical.
 *
 * As duas telas que existem aqui são as que têm VALIDADOR, e é por isso que
 * existem: uma rubrica com incidência incoerente ou uma fórmula de CCT
 * malformada só se manifestariam meses depois, no cálculo ou na malha.
 */
export function Cadastros() {
  return (
    <div className="tela">
      <h1>Cadastros</h1>
      <ValidadorIncidencias />
      <ValidadorFormula />
      <ConfiguracaoCertificado />
    </div>
  );
}

const CODIGOS_CP = ['00', '11', '12', '21', '31', '32', '51', '52', '91', '92', '97'];
const CODIGOS_IRRF = ['00', '11', '12', '13', '31', '32', '33', '41', '46', '51', '52', '53'];
const CODIGOS_FGTS = ['00', '11', '12', '21', '91'];

function ValidadorIncidencias() {
  const [rubrica, setRubrica] = useState({
    codigo: 'NOVA_RUBRICA',
    tpRubr: '1' as '1' | '2' | '3' | '4',
    codIncCP: '11',
    codIncIRRF: '11',
    codIncFGTS: '11',
  });

  const validacao = useQuery({
    queryKey: ['validar-incidencias', rubrica],
    queryFn: () => api.rubricas.validarIncidencias.query(rubrica),
  });

  return (
    <Cartao titulo="Rubrica — validador de incidências">
      <p className="sutil">
        A base de cada tributo é <em>Σ(vencimentos com o código) − Σ(descontos com o código)</em>.
        Por isso o desconto de faltas usa <code>codIncCP 11</code> (reduz a base) e o desconto de
        INSS usa <code>51</code> (não reduz a base que o gerou).
      </p>

      <div className="formulario">
        <label>
          Código
          <input
            value={rubrica.codigo}
            onChange={(e) => setRubrica({ ...rubrica, codigo: e.target.value })}
          />
        </label>

        <label>
          Tipo
          <select
            value={rubrica.tpRubr}
            onChange={(e) =>
              setRubrica({ ...rubrica, tpRubr: e.target.value as '1' | '2' | '3' | '4' })
            }
          >
            <option value="1">1 — Vencimento</option>
            <option value="2">2 — Desconto</option>
            <option value="3">3 — Informativa</option>
            <option value="4">4 — Informativa dedutora</option>
          </select>
        </label>

        <label>
          codIncCP
          <select
            value={rubrica.codIncCP}
            onChange={(e) => setRubrica({ ...rubrica, codIncCP: e.target.value })}
          >
            {CODIGOS_CP.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>

        <label>
          codIncIRRF
          <select
            value={rubrica.codIncIRRF}
            onChange={(e) => setRubrica({ ...rubrica, codIncIRRF: e.target.value })}
          >
            {CODIGOS_IRRF.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>

        <label>
          codIncFGTS
          <select
            value={rubrica.codIncFGTS}
            onChange={(e) => setRubrica({ ...rubrica, codIncFGTS: e.target.value })}
          >
            {CODIGOS_FGTS.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
      </div>

      {validacao.data ? (
        validacao.data.coerente ? (
          <Alerta severidade="INFO" titulo="Incidências coerentes">
            Pronta para ir ao S-1010.
          </Alerta>
        ) : (
          <>
            {validacao.data.problemas.map((p, i) => (
              <Alerta key={i} severidade="ATENCAO" titulo="Revisar">
                {p}
              </Alerta>
            ))}
          </>
        )
      ) : null}
    </Cartao>
  );
}

function ValidadorFormula() {
  const [formula, setFormula] = useState('max(piso, salarioBase) * 0.05');

  const validacao = useQuery({
    queryKey: ['validar-formula', formula],
    queryFn: () => api.configSindical.validarFormula.query({ formula }),
  });

  return (
    <Cartao titulo="Config sindical — validador de fórmula">
      <p className="sutil">
        Fórmulas da CCT são avaliadas por um interpretador fechado (aritmética, parênteses e as
        funções <code>min</code>, <code>max</code>, <code>round</code>, <code>abs</code>) — não por{' '}
        <code>eval</code>. Editar um cadastro nunca executa código.
      </p>

      <label className="formulario__linha">
        Fórmula
        <input value={formula} onChange={(e) => setFormula(e.target.value)} spellCheck={false} />
      </label>

      {validacao.data ? (
        validacao.data.valida ? (
          <Alerta severidade="INFO" titulo="Fórmula válida" />
        ) : (
          <Alerta severidade="BLOQUEANTE" titulo="Fórmula inválida">
            {validacao.data.erro}
          </Alerta>
        )
      ) : null}

      {validacao.data ? (
        <p className="sutil">
          Variáveis disponíveis:{' '}
          {validacao.data.variaveisDisponiveis.map((v) => (
            <code key={v} className="chip">
              {v}
            </code>
          ))}
        </p>
      ) : null}
    </Cartao>
  );
}

function ConfiguracaoCertificado() {
  return (
    <Cartao titulo="Certificado digital e SST">
      <div className="grade-dois">
        <div>
          <h3>Certificado (DEC-02)</h3>
          <p>
            <Etiqueta texto="PROCURAÇÃO" tom="neutro" /> — assinatura com o certificado do
            escritório + procuração eletrônica registrada no eSocial/e-CAC.
          </p>
          <p className="sutil">
            O material do certificado fica no vault (envelope KMS), nunca em disco plano nem em
            variável de ambiente. A fila de transmissão resolve o certificado pelo{' '}
            <code>empresaId</code> no momento do envio — não existe certificado padrão implícito.
          </p>
          <Alerta severidade="ATENCAO" titulo="PE-05 — certificado de teste em uso">
            O ambiente está com um A1 autoassinado gerado localmente. O transmissor BLOQUEIA o uso
            dele em produção; substitua pelo certificado real antes do corte.
          </Alerta>
        </div>

        <div>
          <h3>SST (DEC-03)</h3>
          <p>
            <Etiqueta texto="EXTERNO" tom="neutro" /> — a clínica com procuração envia S-2210,
            S-2220 e S-2240.
          </p>
          <p className="sutil">
            Neste modo o Via Folha <strong>não gera</strong> eventos SST; o engine rejeita qualquer
            tentativa. O painel apenas confere se o terceiro transmitiu, e ASO vencido vira alerta,
            não bloqueio.
          </p>
        </div>
      </div>
    </Cartao>
  );
}
